import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Betragsfeld, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, toast } from '@elbklar/ui';

import { SelectField, TextField } from '@/components/Felder';
import { recurringApi } from '@/lib/api';
import { heuteIso } from '@/lib/format';
import { betragFehler, ohneVorzeichen } from '@/lib/betrag';
import { datum, ganzzahl, hatFehler, hoechstens, nichtVor, ohne, pflicht, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import { STEUERSAETZE, steuerHinweis } from '@/lib/steuer';
import type { Category, RecurringItem } from '@/types';

export const INTERVALLE = [
  { value: 'monthly', label: 'monatlich' },
  { value: 'quarterly', label: 'quartalsweise' },
  { value: 'yearly', label: 'jährlich' },
];

type Feld = 'name' | 'betrag' | 'tag' | 'start' | 'ende';

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  kategorien: Category[];
  onGespeichert: () => void;
  /** Gesetzt = bearbeiten, leer = neu anlegen. */
  posten?: RecurringItem | null;
}

const LEER = {
  name: '',
  richtung: 'aus' as 'aus' | 'ein',
  /** Decimal-Text aus dem Betragsfeld, `null` solange keine Zahl dasteht. */
  betrag: '' as string | null,
  intervall: 'monthly',
  tag: '1',
  start: heuteIso(),
  ende: '',
  kategorie: '',
  steuersatz: '0',
};

function ausPosten(posten: RecurringItem) {
  return {
    name: posten.name,
    richtung: (Number(posten.amount) < 0 ? 'aus' : 'ein') as 'aus' | 'ein',
    betrag: ohneVorzeichen(posten.amount) as string | null,
    intervall: posten.interval,
    tag: String(posten.day_of_month),
    start: posten.start_date,
    ende: posten.end_date ?? '',
    kategorie: posten.category_id ?? '',
    steuersatz: String(Number(posten.tax_rate ?? 0)),
  };
}

export function RecurringItemDialog({
  offen,
  onSchliessen,
  kategorien,
  onGespeichert,
  posten,
}: Props) {
  const [formular, setFormular] = useState(LEER);
  const [fehler, setFehler] = useState<Fehler<Feld>>({});

  const bearbeiten = Boolean(posten);

  useEffect(() => {
    if (!offen) return;
    setFormular(posten ? ausPosten(posten) : LEER);
    setFehler({});
  }, [offen, posten]);

  /** Wert setzen und den Fehler des Felds zurücknehmen; ein neuer Beginn erledigt auch „Nicht vor dem Beginn“. */
  const setze = (feld: Feld, wert: string) => {
    setFormular({ ...formular, [feld]: wert });
    setFehler((f) => (feld === 'start' ? ohne(ohne(f, 'start'), 'ende') : ohne(f, feld)));
  };

  const speichern = useMutation({
    mutationFn: () => {
      // Decimal-Text aus dem Betragsfeld, von `absenden` geprüft.
      const betrag = ohneVorzeichen(formular.betrag ?? '');
      const nutzdaten = {
        name: formular.name.trim(),
        amount: formular.richtung === 'aus' ? `-${betrag}` : betrag,
        interval: formular.intervall,
        day_of_month: Number(formular.tag),
        start_date: formular.start,
        end_date: formular.ende || null,
        category_id: formular.kategorie || null,
        tax_rate: formular.steuersatz,
      };

      return posten
        ? recurringApi.update(posten.id, nutzdaten)
        : recurringApi.create(nutzdaten);
    },
    onSuccess: () => {
      toast.success(bearbeiten ? 'Posten geändert' : 'Fixkostenposten angelegt');
      if (!bearbeiten) setFormular(LEER);
      onGespeichert();
      onSchliessen();
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  // Grenzen aus RecurringItemCreate (finance-api): Name 1–255 Zeichen, Tag
  // 1–28, Ende nicht vor dem Beginn.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const unlesbar = unlesbareFelder(event.currentTarget);
    const neu: Fehler<Feld> = {
      name: pflicht(formular.name, 'Name fehlt') ?? hoechstens(formular.name, 255),
      betrag: betragFehler(formular.betrag, { nichtNull: true }),
      tag: ganzzahl(formular.tag, { min: 1, max: 28, pflicht: true, unlesbar: unlesbar.has('tag') }),
      start: datum(formular.start, { unlesbar: unlesbar.has('start') }),
      ende:
        datum(formular.ende, { leerErlaubt: true, unlesbar: unlesbar.has('ende') }) ??
        nichtVor(formular.ende, formular.start, 'Nicht vor dem Beginn'),
    };
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate();
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onSchliessen()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{bearbeiten ? 'Fixkostenposten bearbeiten' : 'Fixkostenposten anlegen'}</DialogTitle>
        </DialogHeader>
        <form noValidate onSubmit={absenden} className="space-y-4">
          <TextField
            label="Name"
            placeholder="z. B. Büro-Miete"
            value={formular.name}
            onChange={(e) => setze('name', e.target.value)}
            error={fehler.name}
            required
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label="Richtung"
              options={[
                { value: 'aus', label: 'Kosten' },
                { value: 'ein', label: 'Einnahme' },
              ]}
              value={formular.richtung}
              onChange={(e) =>
                setFormular({
                  ...formular,
                  richtung: e.target.value as 'aus' | 'ein',
                })
              }
            />
            <Betragsfeld
              label="Betrag brutto (€)"
              placeholder="850"
              value={formular.betrag ?? ''}
              onChange={(betrag) => {
                setFormular({ ...formular, betrag });
                setFehler((f) => ohne(f, 'betrag'));
              }}
              hinweis={(wert) => steuerHinweis(wert, formular.steuersatz, 'Ohne Vorzeichen, rechnen erlaubt: 850*1,19')}
              error={fehler.betrag}
              required
            />
          </div>

          <SelectField
            label="Umsatzsteuer"
            options={STEUERSAETZE}
            value={formular.steuersatz}
            onChange={(e) => setFormular({ ...formular, steuersatz: e.target.value })}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label="Rhythmus"
              options={INTERVALLE}
              value={formular.intervall}
              onChange={(e) => setFormular({ ...formular, intervall: e.target.value })}
            />
            <TextField
              label="Am Tag"
              type="number"
              name="tag"
              min={1}
              max={28}
              hint="1–28, damit jeder Monat ihn hat"
              value={formular.tag}
              onChange={(e) => setze('tag', e.target.value)}
              error={fehler.tag}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Ab"
              type="date"
              name="start"
              value={formular.start}
              onChange={(e) => setze('start', e.target.value)}
              error={fehler.start}
              required
            />
            <TextField
              label="Bis (optional)"
              type="date"
              name="ende"
              value={formular.ende}
              onChange={(e) => setze('ende', e.target.value)}
              error={fehler.ende}
            />
          </div>

          <SelectField
            label="Kategorie"
            options={[{ value: '', label: 'ohne Kategorie' }, ...kategorien.map((k) => ({ value: k.id, label: k.name }))]}
            value={formular.kategorie}
            onChange={(e) => setFormular({ ...formular, kategorie: e.target.value })}
          />

          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={onSchliessen}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" loading={speichern.isPending}>
              {bearbeiten ? 'Posten speichern' : 'Posten anlegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
